import { useEffect, useState } from 'react';
import { MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import type { HrWorkflowRecord, HrWorkflowView, OfferTerms, Person } from '@vigeo/shared';
import { LegacyOffers, NoOfferYet, OfferEditor, PaperOfferForm, useHrWorkflow } from '@/features/hr-workflow';
import { OfferStageSection } from '../OfferStageSection.js';
import { offerStageState, type OfferStageRequest } from '../offerStageState.js';
import { ReviewOffer } from './ReviewOffer.js';
import { DraftSaved, OfferExpired, SentBack, SentWaiting } from './OfferStatusStates.js';
import { NewOfferForHire } from './NewOfferForHire.js';
import { SendOffer } from './SendOffer.js';
import { DeliveryNotice } from './DeliveryNotice.js';

type OfferRecord = Extract<HrWorkflowRecord, { kind: 'offer' }>;

/**
 * A new draft that starts from an earlier offer's details. The letter is NOT carried over — the
 * old upload belongs to the old offer — and the accept-by date is reset, because the old one is
 * the reason there is a new draft.
 */
function startFrom(from: OfferRecord, responseDays: number): OfferTerms {
  const { letterUploadId, ...terms } = from.terms;
  void letterUploadId;
  return { ...terms, acceptanceDeadline: new Date(Date.now() + responseDays * 86_400_000).toISOString() };
}

/**
 * Offer and stage for a candidate: ONE state at a time, each its approved frame, with one primary
 * action (#440). Replaces the offers block of `HrWorkflowPanel`.
 *
 * Which state is `offerStageState`'s answer, read from the offer standings, the accepted offer and
 * the offer steps. Nothing stored here says where an offer is, so the screen cannot disagree with
 * the record.
 *
 * `OfferStageSection` is always mounted: it draws frames 2.1, 2.2 and 2.5 itself, and for every
 * other state it draws only the notice it owns, which must outlive the state that raised it.
 */
export function OfferStageStates({ person, canEdit, roleLabel, onGoToDocuments, initialRequest, onRequestConsumed }: {
  readonly person: Person;
  readonly canEdit: boolean;
  readonly roleLabel: string | null;
  readonly onGoToDocuments?: () => void;
  /**
   * A request that arrived in the URL — the Overview's "Draft the offer" (#428), or a link from the
   * standalone Review and Send routes. Read as a request, not as initial state: this section stays
   * mounted once visited, so the request can arrive after the first render.
   */
  readonly initialRequest?: OfferStageRequest;
  /** Called once a request has been acted on, so the caller can drop it from the URL. */
  readonly onRequestConsumed?: () => void;
}) {
  const { query, mutation, run } = useHrWorkflow(person);
  const [requested, setRequested] = useState<OfferStageRequest>(null);
  const [delivery, setDelivery] = useState<NonNullable<HrWorkflowView['offerDelivery']> | null>(null);
  // The request's identity as a string, so the effect depends on what was asked for rather than on
  // a fresh object the caller builds every render.
  const requestKey = initialRequest == null ? null : initialRequest.kind === 'draft' ? `draft:${initialRequest.recordId ?? ''}` : 'paper';
  useEffect(() => {
    if (requestKey === null) return;
    // Not spent while nobody is known to be an admin: /api/me may not have answered yet, and spending the
    // request then drops it from the URL before the editor can open (#438). A viewer's request stays
    // in the URL, unused, until they leave the page.
    if (!canEdit) return;
    setRequested(requestKey === 'paper' ? { kind: 'paper' } : { kind: 'draft', recordId: requestKey.slice('draft:'.length) || null });
    onRequestConsumed?.();
  }, [requestKey, canEdit, onRequestConsumed]);

  const section = <OfferStageSection person={person} canEdit={canEdit} roleLabel={roleLabel} onGoToDocuments={onGoToDocuments} />;
  if (query.isPending) return <>{section}<Spinner label="Loading HR workflow" /></>;
  const data = query.data;
  if (query.isError || data === undefined) return <>{section}<MessageBar intent="error"><MessageBarBody>{query.error?.message ?? 'HR workflow unavailable.'}</MessageBarBody></MessageBar></>;

  const disabled = !canEdit || mutation.isPending;
  const handle = (command: Parameters<typeof run>[0]) => { void run(command).catch(() => {}); };
  const now = new Date().toISOString();
  const state = offerStageState(person, data, now, requested);
  const withdraw = (record: OfferRecord) => handle({ action: 'withdraw-offer', recordId: record.id, expectedRevision: record.revision });
  const draftOffer = (record: OfferRecord | null) => setRequested({ kind: 'draft', recordId: record?.id ?? null });
  const close = () => setRequested(null);
  const saveOffer = async (terms: OfferTerms, editing: OfferRecord | null) => {
    await run(editing
      ? { action: 'edit-offer', recordId: editing.id, expectedRevision: editing.revision, terms }
      : { action: 'draft-offer', terms });
    close();
  };

  /*
   * Each case is a slot for a screen: the five states still being designed (draft saved, sent and
   * waiting, sent back, withdrawn or expired, paper-signed) all arrive in `legacy` today, and each
   * gets its own case here when its frame is approved.
   */
  const body = (() => {
    switch (state.kind) {
      case 'stage-section': return null;
      case 'no-offer': return <NoOfferYet canEdit={canEdit} busy={disabled} onDraft={() => draftOffer(null)} onRecordPaper={() => setRequested({ kind: 'paper' })} />;
      case 'enter-offer': return canEdit
        ? <OfferEditor key={state.editing?.id ?? state.from?.id ?? 'new'} person={person}
            initial={state.editing?.terms ?? (state.from === null ? undefined : startFrom(state.from, data.settings.defaultOfferResponseDays))}
            settings={data.settings} busy={disabled} returnsToDraft={state.editing?.status === 'reviewed'}
            onSave={(terms) => saveOffer(terms, state.editing)} onCancel={close} />
        : null;
      case 'draft-saved': return <DraftSaved person={person} record={state.record} canEdit={canEdit} busy={disabled}
        onEdit={() => draftOffer(state.record)} onReview={() => setRequested({ kind: 'review' })} />;
      case 'sent-waiting': return <SentWaiting person={person} record={state.record} step={data.offerSteps.find((step) => step.id === state.record.instanceId) ?? null}
        canEdit={canEdit} busy={disabled} now={now} onWithdraw={() => withdraw(state.record)} />;
      case 'sent-back': return <SentBack person={person} record={state.record} step={state.step} canEdit={canEdit} busy={disabled} now={now} onWithdraw={() => withdraw(state.record)} />;
      case 'expired': return <OfferExpired person={person} record={state.record} canEdit={canEdit} busy={disabled}
        onDraftNew={() => setRequested({ kind: 'draft', recordId: null, fromRecordId: state.record.id })} />;
      case 'past-candidate-idle': return <NewOfferForHire canEdit={canEdit} busy={disabled} hasSigned={state.from !== null}
        onDraft={() => setRequested({ kind: 'draft', recordId: null, ...(state.from === null ? {} : { fromRecordId: state.from.id }) })} />;
      case 'paper-offer': return canEdit ? <PaperOfferForm person={person} disabled={disabled} run={run} onDone={close} onCancel={close} /> : null;
      case 'review-offer': return <ReviewOffer person={person} record={state.record} workflow={data} canEdit={canEdit} onEditTerms={() => draftOffer(state.record)} onBack={close} onReviewed={close} />;
      case 'send-offer': return <SendOffer person={person} record={state.record} workflow={data} canEdit={canEdit} onSent={setDelivery} onRecordPaper={() => setRequested({ kind: 'paper' })} onEdit={() => draftOffer(state.record)} />;
      case 'legacy': return <LegacyOffers person={person} data={data} canEdit={canEdit} disabled={disabled} run={run} handle={handle} onDraftOffer={draftOffer} />;
    }
  })();

  return <>
    {section}
    {mutation.isError ? <MessageBar intent="error"><MessageBarBody>{mutation.error.message}</MessageBarBody></MessageBar> : null}
    {/*
      Sending moves the offer on, which replaces the Send screen before its own confirmation can be
      read. The result is kept here so HR still learns whether the email went out.
    */}
    {delivery === null ? null : <DeliveryNotice person={person} delivery={delivery} canEdit={canEdit} onDismiss={() => setDelivery(null)} />}
    {body}
  </>;
}
